import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, viewChild, type ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { NgpSelectPortal } from 'ng-primitives/select';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArchiveBoxXMark,
  heroArrowDownTray,
  heroArrowLeft,
  heroArrowUpTray,
  heroChevronDown,
  heroChevronLeft,
  heroChevronRight,
  heroChevronUp,
  heroChevronUpDown,
  heroListBullet,
  heroPhoto,
  heroPlus,
  heroShoppingBag,
  heroSparkles,
  heroSquares2x2,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import {
  ListSelection,
  ToastrService,
  UiBadge,
  UiBulkBar,
  UiButton,
  UiCheckbox,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiEmptyState,
  UiFilePicker,
  UiFilterBar,
  UiFilterCluster,
  UiFilterField,
  UiFilterLabel,
  UiInput,
  UiLabel,
  UiOptionRow,
  UiOptionRowCopy,
  UiOptionRowIcon,
  UiPageHeader,
  UiPanelCard,
  UiResourceCardSkeleton,
  UiResourceGrid,
  UiResizableColumns,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSortHeader,
  UiTextarea,
  UiToggleGroup,
  UiToggleGroupItem,
} from '../../../shared/ui';
import { FilesApiService } from '../../../core/api/files-api.service';
import type { RevenueProduct } from '../../../core/api/generated/models/RevenueProduct';
import type { UiBulkAction, UiBulkActionEvent, UiSortChange } from '../../../shared/ui';
import { ProductCardComponent } from './product-card.component';
import { RevenueApiService, type ProductListParams } from './revenue-api.service';
import { PRODUCT_TYPES, PRODUCT_TYPE_ICONS, productTypeIcon, productTypeLabel } from './product-types.catalog';
import { formatProductPrice } from './product-billing.utils';
import { uploadProductImage, validateProductImage } from './product-image-upload';

type StatusFilter = 'active' | 'archived' | 'all';
type SortBy = 'created_at' | 'name';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'table' | 'grid';

interface ProductDraft {
  name: string;
  description: string;
  type: string | null;
}

interface ProductCardModel extends RevenueProduct {
  in_use_by_active_sales?: boolean;
}

const VIEW_STORAGE_KEY = 'revenue:products:viewMode';

@Component({
  selector: 'app-produtos-list-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NgIcon,
    RouterLink,
    UiBulkBar,
    UiBadge,
    UiButton,
    UiCheckbox,
    UiDataTable,
    UiDataTableCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiEmptyState,
    UiFilePicker,
    UiFilterBar,
    UiFilterCluster,
    UiFilterField,
    UiFilterLabel,
    UiInput,
    UiLabel,
    UiOptionRow,
    UiOptionRowCopy,
    UiOptionRowIcon,
    UiPageHeader,
    UiPanelCard,
    UiResizableColumns,
    UiResourceCardSkeleton,
    UiResourceGrid,
    ProductCardComponent,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSortHeader,
    NgpSelectPortal,
    UiTextarea,
    UiToggleGroup,
    UiToggleGroupItem,
  ],
  providers: [
    provideIcons({
      heroArchiveBoxXMark,
      heroArrowDownTray,
      heroArrowLeft,
      heroArrowUpTray,
      heroChevronDown,
      heroChevronLeft,
      heroChevronRight,
      heroChevronUp,
      heroChevronUpDown,
      heroListBullet,
      heroPhoto,
      heroPlus,
      heroShoppingBag,
      heroSparkles,
      heroSquares2x2,
      heroXMark,
      ...PRODUCT_TYPE_ICONS,
    }),
  ],
  templateUrl: './revenue-catalog.page.html',
  styleUrl: './revenue-catalog.page.css',
})
export class RevenueCatalogPage {
  private readonly api = inject(RevenueApiService);
  private readonly files = inject(FilesApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly confirmDialog = inject(UiConfirmDialogService);

  readonly productTypes = PRODUCT_TYPES;
  readonly productTypeIcon = productTypeIcon;
  readonly productTypeLabel = productTypeLabel;
  readonly formatProductPrice = formatProductPrice;

  // ── State ──
  readonly loading = signal(false);
  readonly products = signal<ProductCardModel[]>([]);

  readonly viewMode = signal<ViewMode>(this.loadViewMode());
  readonly selection = new ListSelection();

  // Filters
  readonly search = signal('');
  readonly statusFilter = signal<StatusFilter>('active');
  readonly typeFilter = signal<string | null>(null);
  readonly sortBy = signal<SortBy>('created_at');
  readonly sortOrder = signal<SortOrder>('desc');

  // Pagination
  readonly page = signal(1);
  readonly pageSize = signal(25);
  readonly total = signal(0);
  readonly totalPages = signal(0);
  readonly hasNext = signal(false);
  readonly hasPrev = signal(false);

  readonly selectionCount = computed(() =>
    this.selection.allMatching() ? this.total() : this.selection.count(),
  );

  readonly bulkActions: UiBulkAction[] = [
    { id: 'export', label: 'Exportar CSV', icon: 'heroArrowDownTray' },
    { id: 'archive', label: 'Arquivar', icon: 'heroArchiveBoxXMark', tone: 'danger' },
  ];

  readonly pageRangeLabel = computed(() => {
    const t = this.total();
    if (t === 0) return '0 de 0';
    const start = (this.page() - 1) * this.pageSize() + 1;
    const end = Math.min(start + this.products().length - 1, t);
    return `${start}–${end} de ${t}`;
  });

  // Create flow — inline panel above the grid, 2 steps.
  readonly createMode = signal<'closed' | 'type' | 'form'>('closed');
  readonly productDraft = signal<ProductDraft>({ name: '', description: '', type: null });
  readonly coverFile = signal<File | null>(null);
  readonly submitting = signal(false);
  readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  readonly statusOptions: ReadonlyArray<{ value: StatusFilter; label: string }> = [
    { value: 'active', label: 'Ativos' },
    { value: 'archived', label: 'Arquivados' },
    { value: 'all', label: 'Todos' },
  ];
  readonly sortOptions: ReadonlyArray<{ value: string; label: string; sortBy: SortBy; sortOrder: SortOrder }> = [
    { value: 'recent', label: 'Mais recentes', sortBy: 'created_at', sortOrder: 'desc' },
    { value: 'name_asc', label: 'Nome (A→Z)', sortBy: 'name', sortOrder: 'asc' },
    { value: 'name_desc', label: 'Nome (Z→A)', sortBy: 'name', sortOrder: 'desc' },
  ];
  readonly sortValue = computed(
    () => this.sortOptions.find((o) => o.sortBy === this.sortBy() && o.sortOrder === this.sortOrder())?.value ?? 'recent',
  );

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.reload();
    effect(() => {
      if (this.createMode() === 'form') {
        queueMicrotask(() => this.nameInput()?.nativeElement.focus());
      }
    });
  }

  reload() {
    this.loading.set(true);
    const term = this.search().trim();
    const params: ProductListParams = {
      page: this.page(),
      pageSize: this.pageSize(),
      sort_by: this.sortBy(),
      sort_order: this.sortOrder(),
    };
    const status = this.apiStatus();
    if (status) params.status = status;
    if (this.typeFilter()) params.type = this.typeFilter()!;
    if (term.length >= 2) params.search = term;

    this.api
      .listProducts(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.products.set(resp.data as ProductCardModel[]);
          this.total.set(resp.meta.total);
          this.totalPages.set(resp.meta.totalPages);
          this.hasNext.set(resp.meta.hasNextPage);
          this.hasPrev.set(resp.meta.hasPreviousPage);
          this.loading.set(false);
        },
        error: (err) => {
          this.toastr.error('Não foi possível carregar os produtos.', err?.error?.message);
          this.loading.set(false);
        },
      });
  }

  private resetAndReload(): void {
    this.page.set(1);
    this.selection.clear();
    this.reload();
  }

  // ── View mode ──
  setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch {
      // localStorage indisponível — preferência vale só nesta sessão.
    }
  }
  private loadViewMode(): ViewMode {
    try {
      return localStorage.getItem(VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'table';
    } catch {
      return 'table';
    }
  }

  // ── Pagination ──
  goToPrevPage(): void {
    if (!this.hasPrev() || this.loading()) return;
    this.page.update((p) => Math.max(1, p - 1));
    this.reload();
  }
  goToNextPage(): void {
    if (!this.hasNext() || this.loading()) return;
    this.page.update((p) => p + 1);
    this.reload();
  }

  // ── Filters ──
  onSearchChange(value: string): void {
    this.search.set(value);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    const term = value.trim();
    if (term.length > 0 && term.length < 2) return;
    this.searchDebounce = setTimeout(() => this.resetAndReload(), 300);
  }
  setStatus(value: unknown) {
    this.statusFilter.set(value as StatusFilter);
    this.resetAndReload();
  }
  setSort(value: unknown) {
    const opt = this.sortOptions.find((o) => o.value === value);
    if (!opt) return;
    this.sortBy.set(opt.sortBy);
    this.sortOrder.set(opt.sortOrder);
    this.resetAndReload();
  }
  onColumnSort(change: UiSortChange) {
    this.sortBy.set(change.key as SortBy);
    this.sortOrder.set(change.direction);
    this.resetAndReload();
  }
  setTypeFilter(value: unknown) {
    const v = value as string | null;
    this.typeFilter.set(v === '__all__' ? null : v);
    this.resetAndReload();
  }
  clearFilters() {
    this.search.set('');
    this.statusFilter.set('active');
    this.typeFilter.set(null);
    this.sortBy.set('created_at');
    this.sortOrder.set('desc');
    this.resetAndReload();
  }
  readonly hasFiltersApplied = computed(
    () =>
      this.search().trim().length > 0 ||
      this.statusFilter() !== 'active' ||
      this.typeFilter() !== null ||
      this.sortBy() !== 'created_at' ||
      this.sortOrder() !== 'desc',
  );

  // ── Selection ──
  isSelected(id: string): boolean {
    return this.selection.isSelected(id);
  }
  toggleSelect(id: string, event?: Event): void {
    event?.stopPropagation();
    this.selection.toggle(id);
  }
  selectAllOnPage(checked: boolean): void {
    if (checked) this.selection.selectMany(this.products().map((p) => p.id));
    else this.selection.clear();
  }
  selectAllMatching(): void {
    this.selection.selectAllMatching();
  }
  readonly allOnPageSelected = computed(() => {
    const ids = this.products().map((p) => p.id);
    return ids.length > 0 && ids.every((id) => this.selection.isSelected(id));
  });

  onBulkAction(event: UiBulkActionEvent): void {
    if (event.actionId === 'archive') void this.bulkArchive();
    else if (event.actionId === 'export') this.exportSelected();
  }

  private apiStatus(): 'active' | 'archived' | undefined {
    const s = this.statusFilter();
    return s === 'all' ? undefined : s;
  }

  private bulkBody(): { ids?: string[]; all?: boolean; filters?: ProductListParams } {
    if (this.selection.allMatching()) {
      const filters: ProductListParams = {};
      const status = this.apiStatus();
      if (status) filters.status = status;
      if (this.typeFilter()) filters.type = this.typeFilter()!;
      const term = this.search().trim();
      if (term.length >= 2) filters.search = term;
      return { all: true, filters };
    }
    return { ids: this.selection.ids() };
  }

  async bulkArchive(): Promise<void> {
    const count = this.selectionCount();
    if (count === 0) return;
    const ok = await this.confirmDialog.confirm({
      title: `Arquivar ${count} produto(s)?`,
      description: 'Eles deixam de aparecer no catálogo. Vendas existentes não são afetadas.',
      confirmLabel: 'Arquivar',
      tone: 'danger',
    });
    if (!ok) return;
    this.api
      .bulkArchiveProducts(this.bulkBody())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const failed = result.failed.length;
          if (failed > 0) {
            this.toastr.info(`${result.affected.length} arquivado(s), ${failed} falharam.`);
          } else {
            this.toastr.success(`${result.affected.length} produto(s) arquivado(s).`);
          }
          this.selection.clear();
          this.reload();
        },
        error: (err) => this.toastr.error('Não foi possível arquivar em massa.', err?.error?.message),
      });
  }

  exportSelected(): void {
    const params = this.selection.allMatching()
      ? {
          status: this.apiStatus(),
          type: this.typeFilter() ?? undefined,
          search: this.search().trim().length >= 2 ? this.search().trim() : undefined,
        }
      : { ids: this.selection.ids() };
    this.api
      .exportProducts(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'produtos.csv'),
        error: () => this.toastr.error('Não foi possível exportar.'),
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Create flow ──
  openCreate() {
    this.productDraft.set({ name: '', description: '', type: null });
    this.coverFile.set(null);
    this.createMode.set('type');
  }
  closeCreate() {
    if (this.submitting()) return;
    this.createMode.set('closed');
  }
  selectType(typeKey: string) {
    this.productDraft.update((d) => ({ ...d, type: typeKey }));
    this.createMode.set('form');
  }
  backToTypes() {
    this.createMode.set('type');
  }
  updateDraftField<K extends keyof ProductDraft>(field: K, value: ProductDraft[K]) {
    this.productDraft.update((d) => ({ ...d, [field]: value }));
  }
  onCoverPicked(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (file) {
      const error = validateProductImage(file);
      if (error) {
        this.toastr.error(error);
        if (input) input.value = '';
        return;
      }
    }
    this.coverFile.set(file);
  }
  submitCreate() {
    const draft = this.productDraft();
    if (!draft.name.trim() || this.submitting()) return;
    this.submitting.set(true);
    this.api
      .createProduct({
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        type: draft.type,
        metadata: {},
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          const cover = this.coverFile();
          if (cover) {
            this.uploadCoverThenOpen(created.id, cover, created.name);
          } else {
            this.finishCreate(created.id, created.name);
          }
        },
        error: (err) => {
          this.toastr.error('Não foi possível criar o produto.', err?.error?.message);
          this.submitting.set(false);
        },
      });
  }
  private uploadCoverThenOpen(productId: string, file: File, name: string): void {
    uploadProductImage(this.files, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fileId) => {
          this.api
            .addImage(productId, fileId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.finishCreate(productId, name),
              error: () => this.finishCreate(productId, name),
            });
        },
        error: () => {
          this.toastr.info('Produto criado, mas a foto falhou. Adicione na tela do produto.');
          this.finishCreate(productId, name);
        },
      });
  }
  private finishCreate(productId: string, name: string): void {
    this.toastr.success(`Produto "${name}" criado.`);
    this.submitting.set(false);
    this.createMode.set('closed');
    this.router.navigate(['/produtos', productId]);
  }

  // ── Archive (single) ──
  async archive(product: RevenueProduct, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const ok = await this.confirmDialog.confirm({
      title: 'Arquivar produto?',
      description: `"${product.name}" deixa de aparecer no catálogo. Vendas existentes não são afetadas.`,
      confirmLabel: 'Arquivar',
      tone: 'danger',
    });
    if (!ok) return;
    this.api
      .archiveProduct(product.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastr.success(`"${product.name}" arquivado.`);
          this.reload();
        },
        error: (err) => this.toastr.error('Não foi possível arquivar.', err?.error?.message),
      });
  }
}
